import './UmpirePerformanceSummaryChart.scss';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  UmpireAggregatedPerformanceDataDto,
  TableChartConfigDto,
  UmpirePerformanceSummaryChartDataDto,
  UmpirePerformanceSummaryDataDto,
} from 'types/CodegenReportTypes';
import Table, { Order } from 'components/Table/Table';
import { ChartProps } from 'utilities/chartIdToComponent';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import { TableCell, TableRow, Typography } from '@trackman/web-shared-components';
import { displayDate } from 'utilities/dateHelpers';

export type UmpirePerformanceSummaryChartColumn = keyof UmpirePerformanceSummaryDataDto;

const UmpirePerformanceSummaryChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<UmpirePerformanceSummaryChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<UmpirePerformanceSummaryChartColumn | '' | 'id'>('');

  const rows = useMemo(
    () =>
      chartData?.umpirePerformanceSummaries.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.umpirePerformanceSummaries]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<UmpirePerformanceSummaryChartColumn>(chartConfig.visibleStats);

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: t(column),
      headerShortName: t('short.' + column),
      sortable: true,
      width: 100 / displayedColumns.length + '%',
    }));
  }, [chartConfig.visibleStats]);

  const showPercentageSign = (key: keyof UmpireAggregatedPerformanceDataDto): boolean => {
    return key === 'calledStrikeAccuracy' || key === 'calledBallAccuracy' || key === 'overallAccuracy' || key === 'bufferZoneAccuracy';
  };

  const renderExpandedRow = (row: UmpirePerformanceSummaryDataDto) => {
    return row.gamePerformances.map((gamePerformance) => (
      <TableRow key={[row.umpire, gamePerformance.homeTeam, gamePerformance.awayTeam, gamePerformance.date].join('-')}>
        <TableCell colSpan={3}>
          <Typography variant='label bold' component='div'>
            {displayDate(gamePerformance.date)}
          </Typography>
          <Typography variant='label' component='div'>
            {gamePerformance.homeTeam + ' @ ' + gamePerformance.awayTeam}
          </Typography>
        </TableCell>
        <TableCell>{gamePerformance.pitchesCalled}</TableCell>
        <TableCell>{gamePerformance.bufferZoneAccuracy.toFixed(1)} %</TableCell>
        <TableCell>{gamePerformance.calledStrikeAccuracy.toFixed(1)} %</TableCell>
        <TableCell>{gamePerformance.calledBallAccuracy.toFixed(1)} %</TableCell>
        <TableCell>{gamePerformance.overallAccuracy.toFixed(1)} %</TableCell>
      </TableRow>
    ));
  };

  return (
    <div className='UmpirePerformanceSummaryChart'>
      <Table
        rows={rows}
        pageState={[page, setPage]}
        orderState={[order, setOrder]}
        orderByState={[orderBy, setOrderBy]}
        columns={columns}
        loading={isLoading}
        noFooter
        tileTable
        renderExpandedRow={renderExpandedRow}
      />
      {chartData?.umpirePerformanceSummaryTotal && !isLoading && (
        <div className='UmpirePerformanceSummaryChart-totals'>
          <Typography className='UmpirePerformanceSummaryChart-total-item'>{t('Total')}</Typography>
          {Object.entries(chartData.umpirePerformanceSummaryTotal).map(([key, value]) => (
            <Typography key={key} className='UmpirePerformanceSummaryChart-total-item'>
              {value}
              {showPercentageSign(key as keyof UmpireAggregatedPerformanceDataDto) ? ' %' : ''}
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
};

export default UmpirePerformanceSummaryChart;
